import { GAP_MS, GUARD_TONES, MESSAGE_LENGTH, PREAMBLE, SYMBOL_MS, TONES, decodeMessage } from "./codec";

export const FFT_SIZE = 4096;

const best = (levels) => {
  let index = -1;
  let level = -Infinity;
  let second = -Infinity;
  levels.forEach((value, i) => {
    if (value > level) {
      second = level;
      level = value;
      index = i;
    } else if (value > second) {
      second = value;
    }
  });
  return { index, level, second };
};

/**
 * Turns the levels of the sixteen tones (dB, in symbol order) and the noise floor into a
 * symbol, or null when no single tone stands out: the loudest tone must clear the floor by
 * `minPeakDb` and the runner-up by `minMarginDb`, so noise and in-between frequencies give
 * nothing.
 */
export function chooseSymbol(levels, { minPeakDb = 10, minMarginDb = 6 } = {}) {
  if (!levels) return null;
  const { tones, floor } = levels;
  if (!tones || tones.length !== TONES.length || !Number.isFinite(floor)) return null;
  const { index, level, second } = best(tones);
  if (level - floor < minPeakDb) return null;
  if (level - second < minMarginDb) return null;
  return index;
}

/**
 * Reads the tone levels and the noise floor out of a spectrum (dB per bin, as
 * AnalyserNode.getFloatFrequencyData gives it), in the same shape the worklet posts; used
 * when AudioWorklets are not available. Null when the spectrum does not cover the band.
 */
export function createLevelReader({ sampleRate, fftSize = FFT_SIZE }) {
  const binOf = (hz) => Math.round((hz * fftSize) / sampleRate);
  const toneBins = TONES.map(binOf);
  const bandStart = binOf(Math.min(...GUARD_TONES));
  const bandEnd = binOf(Math.max(...GUARD_TONES));

  const levelAt = (spectrum, bin) =>
    Math.max(spectrum[bin - 1] ?? -Infinity, spectrum[bin] ?? -Infinity, spectrum[bin + 1] ?? -Infinity);

  // the median of the band: the tones occupy a few bins each, most of the band is floor
  const noiseFloor = (spectrum) => {
    const samples = [];
    for (let bin = bandStart; bin <= bandEnd && bin < spectrum.length; bin += 3) samples.push(spectrum[bin]);
    samples.sort((a, b) => a - b);
    return samples.length ? samples[Math.floor(samples.length / 2)] : -Infinity;
  };

  return (spectrum) => {
    if (!spectrum || spectrum.length <= bandEnd) return null;
    return { tones: toneBins.map((bin) => levelAt(spectrum, bin)), floor: noiseFloor(spectrum) };
  };
}

/** The symbol decision made straight from a spectrum. */
export function createSymbolDetector({ sampleRate, fftSize = FFT_SIZE, minPeakDb = 10, minMarginDb = 6 }) {
  const read = createLevelReader({ sampleRate, fftSize });
  return (spectrum) => chooseSymbol(read(spectrum), { minPeakDb, minMarginDb });
}

/**
 * Assembles symbols into messages. Frames with the same symbol form a run; a run ends at a
 * silent frame or at a different symbol and counts once it lasted `confirmFrames` frames.
 * Equal neighbouring symbols whose gap was missed show up as one long run, so the number of
 * symbols in a run is taken from its duration (a symbol and its gap last `symbolMs`). A
 * message starts at the preamble and is reported when it decodes for this session.
 */
export function createDecoder({
  sessionCode,
  onMessage,
  confirmFrames = 2,
  timeoutMs = 700,
  symbolMs = SYMBOL_MS + GAP_MS,
  gapMs = GAP_MS,
}) {
  let frame = [];
  let run = null; // { symbol, frames, startedAt, lastAt }
  let lastActivity = 0;

  const reset = () => {
    frame = [];
    run = null;
  };

  const endRun = () => {
    const ended = run;
    run = null;
    if (!ended || ended.frames < confirmFrames) return;
    if (frame.length === 0 && ended.symbol !== PREAMBLE) return;

    const frameMs = ended.frames > 1 ? (ended.lastAt - ended.startedAt) / (ended.frames - 1) : 0;
    const duration = ended.lastAt - ended.startedAt + frameMs;
    const count = Math.max(1, Math.round((duration + gapMs) / symbolMs));
    for (let i = 0; i < count && frame.length < MESSAGE_LENGTH; i++) frame.push(ended.symbol);
    if (frame.length < MESSAGE_LENGTH) return;

    const message = decodeMessage(frame, sessionCode);
    frame = [];
    if (message) onMessage(message);
  };

  return {
    feed(symbol, timeMs) {
      if ((frame.length > 0 || run) && timeMs - lastActivity > timeoutMs) reset();

      if (symbol === null || symbol === undefined) {
        endRun();
        return;
      }

      lastActivity = timeMs;
      if (run && run.symbol === symbol) {
        run.frames += 1;
        run.lastAt = timeMs;
        return;
      }

      endRun();
      run = { symbol, frames: 1, startedAt: timeMs, lastAt: timeMs };
    },
    reset,
  };
}
